import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import RoundButton from '@deltek/specification-client/core/Buttons/RoundButton'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { AppBarContext } from '@deltek/specification-client/core/Contexts/AppBarContext'
import { CurrentUserInfoQuery } from '@deltek/specification-client/core/GraphQL/Queries/currentUserInfo.queries'
import { CurrentUserInfoResponse } from '@deltek/specification-client/core/Models/currentUserInfo'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    Backdrop,
    Button,
    CircularProgress,
    Typography,
} from '@material-ui/core'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { StartGenerateReport } from 'api/graphQL/mutations/reports.mutations'
import { GetProjectElementNodesQuery } from 'api/graphQL/queries/projectElements.queries'
import {
    GetGeneratedReport,
    GetSmartReportsQuery,
} from 'api/graphQL/queries/projectReport.queries'
import {
    GetGeneratedReportResponse,
    GetGenerateReportStatusResponse,
    StartGenerateReportResponse,
} from 'api/graphQL/types/smartReport.types'
import clsx from 'clsx'
import BuildingBlocksIcon from 'components/Reports/BuildingBlocksIcon'
import FilesIcon from 'components/Reports/FilesIcon'
import ProgressDialog from 'components/ToMoveToSCS/ProgressDialog'
import { GetProjectElementNodesInput } from 'models/getProjectElementNodesInput'
import {
    GenerateReportInput,
    GetBackendProcessStatusInput,
} from 'models/getSmartReportsInput'
import { GetProjectElementNodesResponse } from 'models/projectElementNode'
import { SmartReportDivision, SmartReportsItem } from 'models/projectReports'
import React, { useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { formatLocalDateTime } from 'shared/utils/dateUtils'
import useExportSmartReportAsExcel from 'shared/utils/exportUtils/useExportSmartReportAsExcel'
import { ProjectPageContext } from '../../ProjectPage/components/ProjectPageContext'
import ProjectReportsTable from './ProjectReportsTable'
import { ReportSelectPopover } from './ReportSelectPopover'
import ReportsPageActionBar from './ReportsPageActionBar'
import { hasUsableReportData, isReportDataEmpty } from './reportData'
import { useReportsCache } from './useReportsCache'

const REPORT_STATUS_POLL_INTERVAL_MS = 1000

/**
 * Vertical space taken by the app bar and action bar above the reports content.
 * Subtracted from the viewport height so the table scrolls within the page
 * rather than pushing it.
 */
const CONTENT_HEIGHT_OFFSET_PX = 144

const useStyle = makeStyles((theme: Theme) => ({
    container: {
        height: `100%`,
    },
    projectReportContainer: {
        width: 'calc(100% + 48px)',
        marginLeft: theme.spacing(-3),
        display: 'flex',
        flexDirection: 'row',
        overflow: 'hidden',
        [theme.breakpoints.between('lg', 'md')]: {
            flexDirection: 'column',
        },
        [theme.breakpoints.down('md')]: {
            flexDirection: 'column',
        },
        flex: 1,

        [theme.breakpoints.up(theme.breakpoints.values.xl + 1)]: {
            marginLeft: 0,
            width: '100%',
        },
        [theme.breakpoints.down('lg')]: {
            justifyContent: 'space-between',
        },
        padding: 12,
    },
    /**
     * The page content renders inside `ContentContainer`'s horizontal gutters, but
     * the action bar is a full-bleed grey band. Cancel the gutters the same way
     * `projectReportContainer` above does — including stopping once the content is
     * capped and centred past the xl breakpoint.
     */
    inlineActionBar: {
        width: 'calc(100% + 48px)',
        marginLeft: theme.spacing(-3),

        [theme.breakpoints.up(theme.breakpoints.values.xl + 1)]: {
            marginLeft: 0,
            width: '100%',
        },
    },
    /**
     * `contentHeightOffset` is passed as the `--content-height-offset` CSS
     * variable via inline style rather than a JSS dynamic rule, so changing it
     * only touches this element's style attribute instead of forcing
     * `sheet.update()` on every re-render of this component (a 1000ms polling
     * hot path while a report is generating).
     */
    projectReportInnerContainer: {
        height: `calc(100vh - ${CONTENT_HEIGHT_OFFSET_PX}px - var(--content-height-offset, 0px))`,
    },
    circularProgress: {
        paddingLeft: 5,
    },
    actionIcon: {
        padding: theme.spacing(0),
        margin: 'auto',
    },
    noData: {
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'column',
        paddingTop: theme.spacing(20),
        '& div button.MuiButton-contained': {
            height: 30,
            width: 80,
            padding: 0,
        },
    },
    noDataInnerContainer: {
        textAlign: 'center',
    },
    selectReportBtn: {
        borderRadius: 100,
        fontSize: pxToRem(16),
        backgroundColor: theme.palette.common.white,
        color: '#1899D4',
        padding: '2px 16px',
        '&:hover': {
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            color: theme.palette.common.white,
            backgroundColor: '#1899D4',
        },
    },

    selectReportBtnWithTable: {
        backgroundColor: '#1899D4',
        color: theme.palette.common.white,
        '&:hover': {
            color: '#1899D4',
            backgroundColor: theme.palette.common.white,
        },
    },
    workspaceButton: {
        borderRadius: 100,
        fontSize: pxToRem(18),
        fontWeight: 400,
        marginTop: theme.spacing(5),
        backgroundColor: '#185E8E',
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: '#185E8E',
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        },
    },
    selectReportButton: {
        borderRadius: 100,
        fontSize: pxToRem(12),
        marginTop: theme.spacing(10),
        backgroundColor: '#A68968',
        color: theme.palette.common.white,
        '&:hover': {
            backgroundColor: '#A68968',
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        },
    },
    customWidgetContainer: {
        display: 'flex',
        transition: '100ms',
        padding: '0px 16px',
        marginTop: pxToRem(2),
        flexDirection: 'column',
        alignItems: 'flex-start',
    },
    filesIcon: {
        width: pxToRem(300),
    },
    buildingIcon: {
        width: pxToRem(300),
        marginBottom: pxToRem(20),
    },
    selectedFilter: {
        background: theme.palette.common.white,
        color: '#1899D4',
        fontSize: pxToRem(14),
        fontWeight: 500,
        padding: '2px 14px',
        borderRadius: pxToRem(20),
        display: 'flex',
        alignItems: 'center',
        '& svg': {
            marginLeft: pxToRem(8),
        },
    },
    noElementText: {
        color: '#384F5E',
        fontSize: pxToRem(18),
        fontWeight: 400,
        display: 'block',
    },
    tableTitle: {
        fontSize: pxToRem(28),
        color: theme.palette.common.white,
        fontWeight: 400,
        textTransform: 'capitalize',
    },
    backdrop: {
        zIndex: theme.zIndex.drawer + 1,
        color: theme.palette.common.white,
    },
    clearReportsButton: {
        marginLeft: '10px',
        borderRadius: 100,
        textTransform: 'none',
        backgroundColor: theme.palette.common.white,
        border: '1px solid #1899D4',
        color: '#1899D4',
        '&:hover': {
            boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
            color: '#1899D4',
            backgroundColor: theme.palette.common.white,
        },
    },
}))

/** Props for {@link ReportsPanel}. */
export interface ReportsPanelProps {
    noElementsAddedProp?: boolean
    selectedAssembliesProp?: string[]
    showReportTypeDialogProp?: boolean

    /**
     * Extra vertical space to reserve above the content, in pixels — used by the
     * page shell to account for the sub-tab row when it is displayed.
     */
    contentHeightOffset?: number
}

/**
 * The project Reports sub-tab: report-type selection plus the generated reports
 * table, including report generation, status polling, and Excel export.
 */
const ReportsPanel: React.FC<ReportsPanelProps> = ({
    noElementsAddedProp = true,
    selectedAssembliesProp = [],
    showReportTypeDialogProp = false,
    contentHeightOffset = 0,
}) => {
    const timeZoneOffset = -new Date().getTimezoneOffset() / 60
    const classes = useStyle()
    const history = useHistory()
    const { projectId } = useParams<{ projectId?: string }>()
    const { projectData, setCategoryElementStates } = React.useContext(
        ProjectPageContext
    )
    const { setActionBarVisibility } = React.useContext(AppBarContext)
    const [noElementsAdded, setNoElementsAdded] = useState(noElementsAddedProp) // noElementsAdded flag is used to show the noElementAdded image and workspace button
    const [selectedReports, setSelectedReports] = useState<string[]>([])
    const [lastUpdate, setLastUpdate] = useState<string>()
    const [reportsData, setReportsData] = useState<SmartReportsItem[]>()
    const [reportRowDivisions, setReportRowDivisions] = useState<
        SmartReportDivision[]
    >()
    const {
        addRows,
        markGenerated,
        clearForRegeneration,
        reset: resetReportsCache,
        getRowsForTypes,
        getTypesToFetch,
    } = useReportsCache()
    const fetchingReportTypesRef = useRef<string[]>([])
    const [selectedAssemblies, setSelectedAssemblies] = useState(
        selectedAssembliesProp
    )
    const [showReportTypeDialog, setShowReportTypeDialog] = useState(
        showReportTypeDialogProp
    )
    const divContainer = useRef<HTMLDivElement>()
    const { data: currentUserInfoResponse } = useQuery<CurrentUserInfoResponse>(
        CurrentUserInfoQuery
    )
    const firmId =
        currentUserInfoResponse?.currentUserInfo?.account?.accountId ?? ''
    const [isInProgress, setIsInProgress] = React.useState<boolean>(false)
    const [tableDataLoading, setTableDataLoading] = React.useState<boolean>(
        true
    )
    const [instanceId, setInstanceId] = React.useState<string>()
    const [progress, setProgress] = React.useState<number>(0)
    const [
        isLoadingGeneratedReports,
        setIsLoadingGeneratedReports,
    ] = React.useState<boolean>(false)
    const [inputContentFilter, setInputContentFilter] = React.useState({
        limit: 1000,
        skip: 0,
    })
    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    const handleActionBarStateChange = React.useCallback(
        (stateKey: string, newValue: unknown, persist?: boolean) => {},
        []
    )
    const actionRef = useRef<HTMLDivElement | null>(null)
    const pollingTimeoutRef = useRef<NodeJS.Timeout>()
    const [clearItems, setClearItems] = React.useState(false)

    const clearReportTypeFilter = () => {
        setSelectedReports([])
        setSelectedAssemblies([])
        localStorage.removeItem(`selectedReportTypes-${projectId}`)
        setClearItems(true)
        setShowReportTypeDialog(false)
        resetReportsCache()
        setReportsData(undefined)
        fetchingReportTypesRef.current = []
    }

    const titleWidget = React.useMemo(
        (): React.ReactNode => (
            <GridItem
                ref={actionRef}
                style={{ position: 'relative', display: 'inline-flex' }}
            >
                <RoundButton
                    color="#fff"
                    borderColor="#0A84BB"
                    backgroundColor="#0A84BB"
                    data-testid="report-type"
                    hoverColor="#0A84BB"
                    onClick={() => setShowReportTypeDialog(true)}
                    size="small"
                    aria-haspopup="dialog"
                    aria-expanded={showReportTypeDialog}
                    aria-controls="report-type-dialog"
                >
                    <span
                        aria-label={`Report Type${
                            selectedReports.length > 0
                                ? ` (${selectedReports.length} selected)`
                                : ''
                        }`}
                    >
                        Report Type
                    </span>
                    {selectedReports.length > 0 && (
                        <span style={{ marginLeft: '3px' }}>
                            ({selectedReports.length})
                        </span>
                    )}
                </RoundButton>
                {selectedReports.length > 0 && (
                    <Button
                        variant="outlined"
                        className={classes.clearReportsButton}
                        aria-label="Clear selected reports"
                        onClick={(e) => {
                            e.stopPropagation()
                            clearReportTypeFilter()
                        }}
                    >
                        Clear
                    </Button>
                )}
            </GridItem>
        ),
        [
            selectedReports,
            setShowReportTypeDialog,
            setSelectedReports,
            showReportTypeDialog,
        ]
    )

    React.useEffect(() => {
        if (!noElementsAdded) {
            // Retrieve saved reports for the current project
            const savedReports = localStorage.getItem(
                `selectedReportTypes-${projectId}`
            )

            if (savedReports) {
                // If reports are found in local storage for the current project, parse and set them
                const parsedReports = JSON.parse(savedReports)
                setSelectedReports(parsedReports)
            } else {
                // If no reports are saved for the current project, default it to no selected report
                setSelectedReports([])

                // Save the default reports to local storage for the current project
                localStorage.setItem(
                    `selectedReportTypes-${projectId}`,
                    JSON.stringify([])
                )
            }
        }
    }, [noElementsAdded, projectId])

    const handlePopoverClick = React.useCallback(
        (selectedItems: string[] | undefined) => {
            if (selectedItems) {
                if (selectedItems.length > 0) {
                    // Update selected reports and save them to localStorage
                    setSelectedReports(selectedItems)
                    setSelectedAssemblies(['Assemblies : Unfiltered'])
                    localStorage.setItem(
                        `selectedReportTypes-${projectId}`,
                        JSON.stringify(selectedItems)
                    )
                } else {
                    // Clear selected reports explicitly if the user selects nothing
                    clearReportTypeFilter()
                }
            }

            // Only close the popover; do not clear the state for `undefined` (outside click)
            setShowReportTypeDialog(false)
        },
        [projectId]
    )

    const switchToWorkspace = React.useCallback(() => {
        history.push({
            pathname: `/projects/${projectId}/workspace`,
            state: {
                fromStudio: false,
            },
        })
    }, [])

    const [startReportGenerating] = useMutation<
        StartGenerateReportResponse,
        {
            input: Partial<GenerateReportInput>
        }
    >(StartGenerateReport, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            setInstanceId(data?.generateReport?.instanceId)
        },
        onError: (error) => {
            setIsInProgress(false)
        },
    })

    const [getGeneratedReport] = useLazyQuery<
        GetGeneratedReportResponse,
        {
            input: Partial<GenerateReportInput>
        }
    >(GetGeneratedReport, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            const result = data?.getGeneratedReports?.result

            if (
                hasUsableReportData(
                    result?.reportRows,
                    result?.reportRowDivisions
                )
            ) {
                setReportRowDivisions(result?.reportRowDivisions ?? [])
                addRows(result?.reportRows ?? [])
            }

            if (isReportDataEmpty(result?.reportRows)) {
                generateReportsTableData(false)
            } else {
                fetchingReportTypesRef.current = []
            }

            if (result?.lastUpdate) {
                const formattedDate = formatLocalDateTime(result.lastUpdate)
                setLastUpdate(formattedDate)
            }
            setIsLoadingGeneratedReports(false)
            setTableDataLoading(false)
        },
        onError: (error) => {
            setIsLoadingGeneratedReports(false)
            setTableDataLoading(false)
            fetchingReportTypesRef.current = []
        },
    })

    const [getSmartReports, { loading: getReportsLoading }] = useLazyQuery<
        GetGenerateReportStatusResponse,
        {
            input: Partial<GetBackendProcessStatusInput>
        }
    >(GetSmartReportsQuery, {
        fetchPolicy: 'network-only',
        onCompleted: (data) => {
            if (data) {
                const state = data?.getGenerateReportStatus?.state
                if (state) {
                    switch (state.toUpperCase()) {
                        case 'INPROGRESS':
                            const progressPercentage =
                                data?.getGenerateReportStatus
                                    ?.progressPercentage
                            if (
                                progressPercentage !== undefined &&
                                progressPercentage !== progress
                            ) {
                                setProgress(progressPercentage)
                            }
                            // still in progress; poll again after a short time
                            if (pollingTimeoutRef.current) {
                                clearTimeout(pollingTimeoutRef.current)
                            }
                            pollingTimeoutRef.current = setTimeout(() => {
                                checkForReportsTableDataStatus()
                            }, REPORT_STATUS_POLL_INTERVAL_MS)
                            break
                        case 'COMPLETE':
                            setProgress(100)
                            setIsInProgress(false)
                            const result = data?.getGenerateReportStatus?.result
                            if (result) {
                                setReportRowDivisions(result.reportRowDivisions)
                                addRows(result.reportRows ?? [])
                                if (result?.lastUpdate) {
                                    const formattedDate = formatLocalDateTime(
                                        result.lastUpdate
                                    )
                                    setLastUpdate(formattedDate)
                                }
                            }
                            // Mark types as done even when no rows returned — prevents infinite re-fetch on empty workspaces.
                            // Ref is kept populated to block re-fetch during the React window before the state update takes effect.
                            markGenerated(fetchingReportTypesRef.current)
                            setTableDataLoading(false)
                            break
                        case 'ERROR':
                            setIsInProgress(false)
                            setTableDataLoading(false)
                            fetchingReportTypesRef.current = []
                            break
                    }
                }
            }
        },
        onError: (err) => {
            setIsInProgress(false)
            setTableDataLoading(false)
            fetchingReportTypesRef.current = []
        },
    })

    const { loading: hasElementInProjectQueryLoading } = useQuery<
        GetProjectElementNodesResponse | null,
        GetProjectElementNodesInput
    >(GetProjectElementNodesQuery, {
        fetchPolicy: 'no-cache',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        variables: {
            input: {
                projectId,
                limit: 10,
                skip: 0,
                onlyElementsAssignedToProject: true,
                projectElementType: Object.keys(
                    ProjectElementType
                ) as ProjectElementType[],
                parentNodeId: 'root',
                isProductFamilyGroupView: true,
            },
        },
        onCompleted: (data: GetProjectElementNodesResponse | null) => {
            if (data) {
                const elements =
                    data?.getProjectElementNodes?.projectElementNodes
                setNoElementsAdded(!elements?.some((el) => el.isInProject))
            }
        },
    })

    const fetchReportsTableData = React.useCallback(
        (reportTypesToFetch?: string[]) => {
            setIsLoadingGeneratedReports(true)
            getGeneratedReport({
                variables: {
                    input: {
                        firmId: firmId ?? '',
                        projectId: projectId,
                        reportTypes: reportTypesToFetch ?? selectedReports,
                        contentFilter: {
                            limit: 10000,
                            skip: 0,
                            aggsFields: [],
                        },
                    },
                },
            })
        },
        [getGeneratedReport, setIsLoadingGeneratedReports, selectedReports]
    )

    const generateReportsTableData = React.useCallback(
        (isManualRegen = false) => {
            // Clear cache only for manual regen of previously generated report types
            if (isManualRegen) {
                clearForRegeneration(selectedReports)
            }

            fetchingReportTypesRef.current = [...selectedReports]
            setIsInProgress(true)
            startReportGenerating({
                variables: {
                    input: {
                        firmId: firmId ?? '',
                        projectId: projectId,
                        reportTypes: selectedReports,
                        contentFilter: {
                            limit: 10000,
                            skip: 0,
                            aggsFields: [],
                        },
                    },
                },
            })
        },
        [
            startReportGenerating,
            setIsInProgress,
            selectedReports,
            clearForRegeneration,
        ]
    )

    const checkForReportsTableDataStatus = React.useCallback(() => {
        if (!getReportsLoading) {
            setIsInProgress(true)
            getSmartReports({
                variables: {
                    input: {
                        instanceId: instanceId,
                    },
                },
            })
        }
    }, [instanceId, setIsInProgress])

    const fetchMoreData = React.useCallback(() => {
        if (!isInProgress) {
            setInputContentFilter({
                ...inputContentFilter,
                skip: inputContentFilter.skip + 500,
            })
            fetchReportsTableData()
        }
    }, [inputContentFilter, isInProgress])

    const { exportItems, loading } = useExportSmartReportAsExcel({
        datatype: 'SmartReport',
        projectName: projectData?.getProject?.name,
    })

    const exportSmartReports = React.useCallback(() => {
        exportItems({
            variables: {
                input: {
                    firmId: firmId ?? '',
                    projectId: projectId,
                    reportTypes: selectedReports,
                    contentFilter: {
                        limit: 10000,
                        skip: 0,
                        aggsFields: [],
                    },
                },
            },
        })
    }, [exportItems, selectedReports])

    React.useEffect(() => {
        if (instanceId) {
            checkForReportsTableDataStatus()
        }
    }, [instanceId])

    React.useEffect(() => {
        // The "Report Type" bar belongs to this panel's content, so it is rendered
        // inline below the Reports/Insights sub-tab row rather than pushed to the
        // shared app header via `setCustomActionBar`. The header renders above all
        // routed page content, which would put the bar above the sub-tabs.
        setActionBarVisibility(false)
        setCategoryElementStates?.({})
    }, [setActionBarVisibility, setCategoryElementStates])

    // Smart fetch/filter based on existing data
    React.useEffect(() => {
        if (!noElementsAdded && selectedReports.length > 0) {
            const cachedData = getRowsForTypes(selectedReports)

            setReportsData(cachedData)

            // Auto-fetch ONLY report types that have NEVER been generated before
            // If a type was generated before but not existing from cache, user must manually regen
            const selectedReportTypesToUpdate =
                getTypesToFetch(selectedReports)

            if (
                selectedReportTypesToUpdate.length > 0 &&
                !isLoadingGeneratedReports &&
                !isInProgress
            ) {
                const alreadyFetching = selectedReportTypesToUpdate.every(
                    (type) => fetchingReportTypesRef.current.includes(type)
                )

                if (!alreadyFetching) {
                    fetchingReportTypesRef.current = [
                        ...selectedReportTypesToUpdate,
                    ]
                    fetchReportsTableData(selectedReportTypesToUpdate)
                }
            } else if (selectedReportTypesToUpdate.length === 0) {
                // All data exists, just filter client-side (no API call)
                setTableDataLoading(false)
            }
        }
        // `getRowsForTypes` / `getTypesToFetch` change identity exactly when the cached
        // rows / generated types change, so this re-runs on the same cache updates it
        // did when that state lived here.
    }, [
        noElementsAdded,
        selectedReports,
        getRowsForTypes,
        getTypesToFetch,
        isLoadingGeneratedReports,
        isInProgress,
        fetchReportsTableData,
    ])

    React.useEffect(() => {
        return () => {
            if (pollingTimeoutRef.current) {
                clearTimeout(pollingTimeoutRef.current)
            }
        }
    }, [])

    return (
        <>
            {!noElementsAdded && (
                <div className={classes.inlineActionBar}>
                    <ReportsPageActionBar
                        currentState={{}}
                        onStateChange={handleActionBarStateChange}
                        title={titleWidget}
                    />
                </div>
            )}
            <div
                className={classes.container}
                data-testid="projectReportContainer"
            >
                <GridContainer
                    direction="row"
                    className={classes.projectReportContainer}
                >
                    <GridItem
                        xs={12}
                        ref={divContainer}
                        className={classes.projectReportInnerContainer}
                        style={
                            {
                                '--content-height-offset': `${contentHeightOffset}px`,
                            } as React.CSSProperties
                        }
                    >
                        {!hasElementInProjectQueryLoading && (
                            <>
                                {!noElementsAdded ? (
                                    selectedReports.length === 0 ? (
                                        <div className={classes.noData}>
                                            <GridItem
                                                className={
                                                    classes.noDataInnerContainer
                                                }
                                            >
                                                <FilesIcon
                                                    className={
                                                        classes.filesIcon
                                                    }
                                                    data-testid="report-noReportTypeSelected"
                                                />
                                            </GridItem>
                                            <Button
                                                variant="contained"
                                                color="default"
                                                disableElevation
                                                data-testid="select-report-type-btn"
                                                className={
                                                    classes.selectReportButton
                                                }
                                                onClick={() =>
                                                    setShowReportTypeDialog(
                                                        true
                                                    )
                                                }
                                            >
                                                Select Report Type
                                            </Button>
                                        </div>
                                    ) : (
                                        <ProjectReportsTable
                                            fetchNextData={fetchMoreData}
                                            fetchData={fetchReportsTableData}
                                            reportsData={reportsData}
                                            generateReport={() =>
                                                generateReportsTableData(true)
                                            }
                                            divisions={reportRowDivisions}
                                            lastUpdate={lastUpdate}
                                            tableDataLoading={tableDataLoading}
                                            isExportLoading={loading}
                                            exportReports={exportSmartReports}
                                        />
                                    )
                                ) : (
                                    <div className={classes.noData}>
                                        <GridItem
                                            className={
                                                classes.noDataInnerContainer
                                            }
                                        >
                                            {/* This will be added later, once we have more that submitals reports */}
                                            {noElementsAdded && false && (
                                                <FilesIcon
                                                    className={
                                                        classes.filesIcon
                                                    }
                                                    data-testid="report-noData"
                                                />
                                            )}
                                            {noElementsAdded && (
                                                <>
                                                    <BuildingBlocksIcon
                                                        className={
                                                            classes.buildingIcon
                                                        }
                                                        data-testid="report-noElementSelected"
                                                    />
                                                    <Typography
                                                        component="span"
                                                        className={
                                                            classes.noElementText
                                                        }
                                                    >
                                                        You Haven't Added Any
                                                        Element to Your Project.
                                                    </Typography>
                                                    <Typography
                                                        component="span"
                                                        className={
                                                            classes.noElementText
                                                        }
                                                    >
                                                        Start Building Your
                                                        Project to Enable
                                                        Reports.
                                                    </Typography>
                                                </>
                                            )}
                                        </GridItem>

                                        <Button
                                            variant="contained"
                                            color="default"
                                            disableElevation
                                            data-testid={
                                                noElementsAdded
                                                    ? 'go-to-workspace-btn'
                                                    : 'select-report-type-btn'
                                            }
                                            className={clsx(
                                                noElementsAdded
                                                    ? classes.workspaceButton
                                                    : classes.selectReportButton
                                            )}
                                            onClick={() =>
                                                noElementsAdded
                                                    ? switchToWorkspace()
                                                    : setShowReportTypeDialog(
                                                          true
                                                      )
                                            }
                                        >
                                            {noElementsAdded
                                                ? 'Go To Workspace'
                                                : 'Select report type'}
                                        </Button>
                                    </div>
                                )}
                            </>
                        )}
                    </GridItem>
                </GridContainer>
            </div>
            <ReportSelectPopover
                anchorElement={actionRef.current}
                isActionPopoverDisplayed={showReportTypeDialog}
                handleActionPopoverClick={handlePopoverClick}
                anchorOrigin={{
                    vertical: (actionRef?.current?.clientHeight ?? 0) + 8,
                    horizontal: (actionRef?.current?.clientWidth ?? 0) + 30,
                }}
                clearTrigger={clearItems}
                resetClearTrigger={() => setClearItems(false)}
            />
            <ProgressDialog
                dialogId={'reportsProgressDialog'}
                dialogName={'Generating your Report'}
                open={isInProgress}
                progressPercentage={progress > 100 ? 100 : progress}
                statusText={'Loading...'}
            />
            <Backdrop
                className={classes.backdrop}
                open={
                    (hasElementInProjectQueryLoading ||
                        isLoadingGeneratedReports) ??
                    false
                }
            >
                <CircularProgress color="inherit" />
            </Backdrop>
        </>
    )
}

export default ReportsPanel
